import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

/**
 * Auth Interceptor
 * - Adds Authorization token to all HTTP requests
 * - Handles 401 Unauthorized responses by logging out the user
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  private isPublicAuthEndpoint(url: string | null): boolean {
    if (!url) return false;
    return (
      url.includes('/Auth/Login') ||
      url.includes('/Auth/Register') ||
      url.includes('/Auth/SendPasswordResetEmail') ||
      url.includes('/Auth/ResetPassword') ||
      url.includes('/Auth/VerifyEmail') ||
      url.includes('/Auth/ResendEmailVerification')
    );
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Add Authorization header if token exists
    const token = this.authService.getToken();
    if (token) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Handle 401 Unauthorized
        if (error.status === 401 && token && !this.isPublicAuthEndpoint(request.url)) {
          console.warn('Unauthorized access - logging out user');
          this.authService.logout();
        }

        // Handle other HTTP errors
        const errorMessage = this.getErrorMessage(error);
        console.error('HTTP Error:', {
          status: error.status,
          message: errorMessage,
          url: error.url,
        });

        return throwError(() => error);
      }),
    );
  }

  /**
   * Extract meaningful error message from HTTP response
   */
  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      return error.error.message;
    } else {
      // Backend error
      return error.error?.message || error.statusText || 'An error occurred';
    }
  }
}
